import type { SourceCatalogEntry } from '@shared/api';
import type { DiscoverySourceId } from '@shared/sources';
import { useQuery } from '@tanstack/react-query';
import { Ban, Check, Lock } from 'lucide-react';
import { Alert, Badge, Spinner } from '../../components/ui/feedback';
import { api } from '../../lib/api';
import { cn } from '../../lib/cn';
import { queryKeys } from '../../lib/query';

export interface StepSourcesProps {
  selected: DiscoverySourceId[];
  onChange: (sources: DiscoverySourceId[]) => void;
  error?: string | undefined;
}

/**
 * Step 1. A source that cannot run is shown but not selectable, with the reason stated — the
 * wizard never lets you build a run that would fail at execution time.
 */
export function StepSources({ selected, onChange, error }: StepSourcesProps) {
  const sources = useQuery({
    queryKey: queryKeys.catalogSources,
    queryFn: () => api.get<SourceCatalogEntry[]>('/api/catalog/sources'),
  });

  if (sources.isPending) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Spinner /> Checking which sources can run…
      </div>
    );
  }

  const toggle = (id: DiscoverySourceId): void => {
    onChange(selected.includes(id) ? selected.filter((value) => value !== id) : [...selected, id]);
  };

  return (
    <div className="space-y-3">
      {error ? <Alert tone="error">{error}</Alert> : null}
      <div className="grid gap-2.5 md:grid-cols-2">
        {sources.data?.map((source) => {
          const id = source.id as DiscoverySourceId;
          const usable = source.implemented && source.status !== 'unavailable';
          const isSelected = selected.includes(id);

          return (
            <button
              key={source.id}
              type="button"
              disabled={!usable}
              aria-pressed={isSelected}
              onClick={() => toggle(id)}
              className={cn(
                'rounded-[var(--radius-panel)] border p-3 text-left transition-colors duration-150',
                isSelected ? 'border-accent bg-accent-subtle' : 'border-border bg-surface hover:border-border-strong',
                !usable && 'cursor-not-allowed opacity-60 hover:border-border',
              )}
            >
              <span className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-2 text-sm font-medium text-foreground">
                  {source.label}
                  {isSelected ? <Check className="h-3.5 w-3.5 text-accent" aria-hidden="true" /> : null}
                </span>
                {!source.implemented ? (
                  <Badge tone="outline">Not in this build</Badge>
                ) : source.status === 'unavailable' ? (
                  <Badge tone="neutral">Unavailable</Badge>
                ) : source.status === 'needs_connection' ? (
                  <Badge tone="warning">Needs connection</Badge>
                ) : (
                  <Badge tone="success">Ready</Badge>
                )}
              </span>
              <span className="mt-1 block text-[13px] text-muted-foreground">{source.tagline}</span>
              {source.status === 'needs_connection' ? (
                <span className="mt-2 flex items-start gap-1.5 text-[13px] text-muted-foreground">
                  <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                  {source.message} You can still select it — connect the key on the Sources page before running.
                </span>
              ) : null}
              {/*
                A source that cannot run must say why on the card. This used to render only for a
                source missing from the build, so an unavailable one showed a green "Ready" badge
                and no reason while refusing to be selected.
              */}
              {source.status === 'unavailable' && source.message ? (
                <span className="mt-2 flex items-start gap-1.5 text-[13px] text-muted-foreground">
                  <Ban className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                  {source.message}
                </span>
              ) : null}
              {!source.implemented && source.message ? (
                <span className="mt-2 block text-[13px] text-muted-foreground">{source.message}</span>
              ) : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}
